/**
 * Recurrence derivation utilities for route stop operations.
 *
 * Centralizes the shared recurrence parsing logic used by all stop parsers.
 * Previously duplicated in mapStopToListItemFormat and mapMasterStopToListItemFormat.
 */
import { PRICED_SERVICE_TYPE } from 'settings/constants/service'
import T from 'T'
import { isOneOffStop } from 'utils/routeStopOperations/oneOff'
import { getPricingPeriod } from 'utils/service'

const ON_REQUEST_LABEL = 'On Request'

// Re-export for convenience within the routeStopOperations module
export { getPricingPeriod }

// BE `daysOfWeek` convention (UMTWRFS). Indexed by JS Date#getDay() (Sun..Sat).
export const DAY_ABBREVIATIONS = ['U', 'M', 'T', 'W', 'R', 'F', 'S']
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

type RecurrenceOnDateStop = {
  pricingPeriod?: string | null
  daysOfWeek?: string | null
  pricedServiceType?: string | null
  isOnRequest?: boolean | null
}

type RecurrenceFields = {
  recurrencePer?: string | number | null
  recurrenceInterval?: string | null
  recurrenceFrequency?: string | number | null
  pricedServiceType?: string | null
  configuredServiceEventType?: string | null
}

/**
 * Returns the single-letter day-of-week abbreviation for a BE service date
 * string (YYYY-MM-DD), or `''` when the string is malformed.
 */
export const dowAbbrForServiceDateString = (serviceDate: unknown) => {
  if (typeof serviceDate !== 'string') return ''
  const [y, m, d] = serviceDate.split('-').map(Number)
  if (!y || !m || !d) return ''
  return DAY_ABBREVIATIONS[new Date(y, m - 1, d).getDay()]
}

/**
 * True when a stop recurs on the given date. On-request stops are always
 * treated as "applies" (they are explicitly assigned to that day). Weekly
 * services check their `daysOfWeek` against the date's abbreviation;
 * monthly/quarterly/etc. fall through to true because day-of-week doesn't
 * determine their cadence.
 *
 * @param {Object} stop - { pricingPeriod, daysOfWeek, pricedServiceType | isOnRequest }
 * @param {Date|string} dateOrIso - JS Date OR BE-format 'YYYY-MM-DD' string
 */
export const hasRecurrenceOnDate = (stop: RecurrenceOnDateStop | null | undefined, dateOrIso: Date | string) => {
  if (!stop) return false
  if (stop.isOnRequest || stop.pricedServiceType === 'On Request') return true
  const pricingPeriod = stop.pricingPeriod || ''
  if (pricingPeriod === 'Daily') return true
  if (pricingPeriod === 'Weekly' || pricingPeriod.includes('Week')) {
    const abbr = dateOrIso instanceof Date ? DAY_ABBREVIATIONS[dateOrIso.getDay()] : dowAbbrForServiceDateString(dateOrIso)
    return Boolean(abbr) && (stop.daysOfWeek || '').includes(abbr)
  }
  return true
}

/**
 * Parses a days-of-week abbreviation string into an array of { day, variant } objects.
 *
 * @param {string} daysOfWeek - e.g. "UMTWRF" where U=Sunday, M=Monday, etc.
 * @param {string} pricingPeriod - e.g. "Weekly", "Daily"
 * @returns {{ day: string, variant: 'active'|'default' }[]}
 */
export const parseDaysOfWeek = (daysOfWeek?: string | null, pricingPeriod?: string | null) => {
  const isDaily = pricingPeriod === 'Daily'
  return DAY_ABBREVIATIONS.map((abbr, index) => ({
    day: DAY_NAMES[index],
    variant: isDaily || (daysOfWeek && daysOfWeek.includes(abbr)) ? 'active' : 'default',
  }))
}

/**
 * `isOffSchedule` marks an on-request service or a one-off on a recurring one; callers skip the pricingPeriod
 * override for it, which would otherwise bury "One off" under the cadence a one-off inherits from its parent.
 */
export const deriveRecurrence = ({
  recurrencePer,
  recurrenceInterval,
  recurrenceFrequency = 1,
  pricedServiceType = '',
  configuredServiceEventType = '',
}: RecurrenceFields) => {
  const pricingPeriod = getPricingPeriod(recurrencePer, recurrenceInterval)
  if (pricedServiceType === PRICED_SERVICE_TYPE.ON_REQUEST) {
    return { pricingPeriod, recurrence: ON_REQUEST_LABEL, showWeekSummary: false, isOffSchedule: true }
  }
  // Only a one-off on a *recurring* service earns the label — an on-request
  // service's events carry the same `One Off` event type but aren't extras.
  if (isOneOffStop({ configuredServiceEventType, pricedServiceType })) {
    return { pricingPeriod, recurrence: T.ONE_OFF, showWeekSummary: false, isOffSchedule: true }
  }
  const showWeekSummary = pricingPeriod === 'Weekly' || pricingPeriod === 'Daily'
  const isRecurring = pricedServiceType === 'Recurring' || !!pricingPeriod

  let recurrence: string
  if (pricingPeriod) {
    recurrence = `${recurrenceFrequency}x | ${pricingPeriod}`
  } else if (isRecurring) {
    recurrence = 'Recurring'
  } else {
    recurrence = ON_REQUEST_LABEL
  }

  return { pricingPeriod, recurrence, showWeekSummary, isOffSchedule: recurrence === ON_REQUEST_LABEL }
}
